import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleManager({ token }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    if (!d) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/availability`, {
        params: { date: d },
        headers: { 'x-admin-token': token },
      });
      setSlots(data.slots || []);
    } catch (e) {
      toast.error('Could not load schedule.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const toggle = async (slot) => {
    try {
      if (slot.blocked) {
        if (slot.type === 'booking') {
          toast.error('This is a real booking. Cancel it from the Bookings tab to free the slot.');
          return;
        }
        await axios.delete(`${API}/admin/availability/unblock`, {
          params: { date, time: slot.time },
          headers: { 'x-admin-token': token },
        });
        toast.success(`Opened ${slot.time}`);
      } else {
        await axios.post(`${API}/admin/availability/block`, { date, time: slot.time }, {
          headers: { 'x-admin-token': token },
        });
        toast.success(`Blocked ${slot.time}`);
      }
      load(date);
    } catch (e) {
      toast.error('Update failed.');
    }
  };

  const blockAll = async () => {
    const open = slots.filter(s => !s.blocked);
    for (const s of open) {
      await axios.post(`${API}/admin/availability/block`, { date, time: s.time }, { headers: { 'x-admin-token': token } }).catch(() => {});
    }
    toast.success('Day blocked.');
    load(date);
  };

  const openAll = async () => {
    const adminBlocked = slots.filter(s => s.blocked && s.type === 'admin_block');
    for (const s of adminBlocked) {
      await axios.delete(`${API}/admin/availability/unblock`, { params: { date, time: s.time }, headers: { 'x-admin-token': token } }).catch(() => {});
    }
    toast.success('Day opened.');
    load(date);
  };

  return (
    <div data-testid="schedule-manager" className="bg-[#141414] border border-white/5 rounded-2xl p-6 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h3 className="font-display text-xl mb-1">Schedule Manager</h3>
          <p className="text-zinc-500 text-sm">Block or open time slots. Clients only see open times when booking.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Date</label>
            <input
              type="date"
              data-testid="schedule-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-blue-500"
            />
          </div>
          <button onClick={blockAll} data-testid="schedule-block-all" className="px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-sm">Block Day</button>
          <button onClick={openAll} data-testid="schedule-open-all" className="px-4 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-sm">Open Day</button>
        </div>
      </div>

      {loading ? (
        <p className="text-zinc-500 text-sm">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {slots.map((s) => {
            const isBooking = s.type === 'booking';
            const isAdminBlock = s.type === 'admin_block';
            return (
              <button
                key={s.time}
                onClick={() => toggle(s)}
                data-testid={`slot-${s.time.replace(/[^a-z0-9]/gi, '')}`}
                className={`p-4 rounded-xl border text-left transition-all ${
                  isBooking
                    ? 'border-blue-500/40 bg-blue-600/15 cursor-not-allowed'
                    : isAdminBlock
                      ? 'border-red-500/30 bg-red-500/10 hover:bg-red-500/20'
                      : 'border-green-500/30 bg-green-500/5 hover:bg-green-500/15'
                }`}
              >
                <div className="text-sm font-medium">{s.time}</div>
                <div className={`text-xs mt-1 flex items-center gap-1 ${
                  isBooking ? 'text-blue-300' : isAdminBlock ? 'text-red-300' : 'text-green-300'
                }`}>
                  {isBooking ? (<><Calendar className="w-3 h-3" /> Booked</>) :
                   isAdminBlock ? (<><Lock className="w-3 h-3" /> Blocked</>) :
                   (<><Unlock className="w-3 h-3" /> Open</>)}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-4 text-xs text-zinc-500">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-500/40 border border-green-500/30" /> Open · clients can book</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-500/40 border border-red-500/30" /> Blocked by you</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-blue-500/40 border border-blue-500/30" /> Real booking</span>
      </div>
    </div>
  );
}
